import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import Logo from "../components/Logo";
import { toast } from "sonner";
import { Trophy, ArrowRight, CheckCircle, Lightning, CreditCard, Heart } from "@phosphor-icons/react";

const TIER_COLORS = {
  Platine: "bg-slate-100 text-slate-900 hard-border",
  Or: "bg-amber-100 text-amber-900 hard-border",
  Argent: "bg-gray-100 text-gray-900 hard-border",
  Bronze: "bg-orange-100 text-orange-900 hard-border",
};

const TPS = 0.05;
const TVQ = 0.09975;

export default function CommanditesPublic() {
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null); // tier object
  const [customAmount, setCustomAmount] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [confirmed, setConfirmed] = useState(null);
  const [form, setForm] = useState({
    sponsor_company: "",
    sponsor_contact_name: "",
    sponsor_email: "",
    sponsor_phone: "",
    sponsor_address: "",
    business_number: "",
    notes: "",
  });

  useEffect(() => {
    document.title = "Commandites — Gala Gladiateurs du Nord · 16 mai 2026";
    api.get("/sponsorships/plans")
      .then(r => setPlan(r.data))
      .catch(() => setPlan(null))
      .finally(() => setLoading(false));
  }, []);

  const pickTier = (tier) => {
    setSelected(tier);
    setCustomAmount("");
    setShowForm(true);
  };

  const pickCustom = () => {
    const amt = parseFloat(customAmount);
    if (!amt || amt < 25) {
      toast.error("Indique un montant d'au moins 25 $.");
      return;
    }
    setSelected({ tier: "Soutien libre", price: amt, perks: "Mention sur réseaux + reçu de commandite" });
    setShowForm(true);
  };

  const breakdownFor = (subtotal) => {
    const tps = +(subtotal * TPS).toFixed(2);
    const tvq = +(subtotal * TVQ).toFixed(2);
    return { subtotal, tps, tvq, total: +(subtotal + tps + tvq).toFixed(2) };
  };

  const submitOrder = async () => {
    if (!form.sponsor_company.trim() || !form.sponsor_contact_name.trim() || !form.sponsor_email.trim()) {
      toast.error("Compagnie, nom et courriel sont requis.");
      return;
    }
    setSubmitting(true);
    try {
      const isCustom = selected.tier === "Soutien libre";
      const payload = {
        ...form,
        plan_id: isCustom ? null : plan.id,
        plan_tier: isCustom ? null : selected.tier,
        custom_amount: isCustom ? selected.price : null,
      };
      const r = await api.post("/sponsorships/orders", payload);
      setConfirmed(r.data);
      setShowForm(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de la création de la commande.");
    } finally {
      setSubmitting(false);
    }
  };

  const startCheckout = async () => {
    if (!confirmed) return;
    setSubmitting(true);
    try {
      const r = await api.post("/sponsorships/checkout/session", {
        order_id: confirmed.id,
        origin_url: window.location.origin,
      });
      if (r.data?.url) window.location.href = r.data.url;
      else toast.error("Impossible d'ouvrir le paiement.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur Stripe.");
    } finally {
      setSubmitting(false);
    }
  };

  // ---------- CONFIRMATION (after order created, before pay) ----------
  if (confirmed) {
    return (
      <div className="min-h-screen bg-background" data-testid="commandites-confirmation">
        <Nav />
        <div className="max-w-2xl mx-auto px-6 py-16">
          <div className="hard-border bg-white p-8 space-y-6">
            <div className="flex items-center gap-3">
              <CheckCircle size={48} weight="fill" className="text-[#0A8754]" />
              <div>
                <div className="ticker text-muted-foreground">COMMANDE RÉSERVÉE</div>
                <div className="font-display font-black text-3xl">{confirmed.order_no}</div>
              </div>
            </div>
            <div className="space-y-2 text-sm">
              <Row label="Compagnie" value={confirmed.sponsor_company} />
              <Row label="Niveau" value={confirmed.plan_tier} />
              <Row label="Sous-total" value={`${confirmed.subtotal_ht.toFixed(2)} $`} />
              <Row label="TPS (5 %)" value={`${confirmed.tps.toFixed(2)} $`} />
              <Row label="TVQ (9,975 %)" value={`${confirmed.tvq.toFixed(2)} $`} />
              <div className="hard-border-t pt-2 mt-2 flex justify-between font-display font-black text-xl">
                <span>Total</span>
                <span className="tabular-nums">{confirmed.total_amount.toFixed(2)} $ CAD</span>
              </div>
            </div>
            <div className="hard-border bg-foreground text-white p-5 space-y-3">
              <div className="flex items-center gap-2 ticker text-xs text-white/70">
                <Lightning size={14} weight="fill" /> PAIEMENT SÉCURISÉ STRIPE
              </div>
              <div className="text-sm text-white/80">
                Paie en ligne pour confirmer ta commandite. Tu recevras un reçu PDF officiel par courriel.
              </div>
              <button
                onClick={startCheckout}
                disabled={submitting}
                className="w-full bg-[#D90429] hover:bg-[#BF0424] text-white py-3 ticker transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="commandite-pay-btn"
              >
                <CreditCard size={18} weight="bold" />
                {submitting ? "Redirection…" : `Payer ${confirmed.total_amount.toFixed(2)} $ maintenant`}
              </button>
              <div className="text-xs text-white/50">
                Mode test : utilise <code className="bg-white/10 px-1">4242 4242 4242 4242</code>.
              </div>
            </div>
            <div className="text-xs text-muted-foreground">
              Confirmation envoyée à <strong>{confirmed.sponsor_email}</strong>.
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ---------- ORDER FORM ----------
  if (showForm && selected) {
    const bd = breakdownFor(selected.price);
    return (
      <div className="min-h-screen bg-background" data-testid="commandites-form-page">
        <Nav />
        <div className="max-w-2xl mx-auto px-6 py-16">
          <button onClick={() => { setShowForm(false); setSelected(null); }} className="ticker text-muted-foreground hover:text-foreground mb-4" data-testid="back-tiers">← Retour aux niveaux</button>
          <div className="hard-border bg-white p-8 space-y-6">
            <div>
              <div className="ticker text-[#D90429]">ÉTAPE 1/2 — TES INFOS</div>
              <h2 className="font-display font-black text-3xl mt-1">Niveau {selected.tier}</h2>
              <p className="text-sm text-muted-foreground mt-2">{selected.perks}</p>
            </div>
            <div className="hard-border p-4 bg-muted/20 space-y-1 text-sm">
              <div className="flex justify-between"><span>Sous-total</span><span className="tabular-nums">{bd.subtotal.toFixed(2)} $</span></div>
              <div className="flex justify-between text-muted-foreground"><span>TPS (5 %)</span><span className="tabular-nums">{bd.tps.toFixed(2)} $</span></div>
              <div className="flex justify-between text-muted-foreground"><span>TVQ (9,975 %)</span><span className="tabular-nums">{bd.tvq.toFixed(2)} $</span></div>
              <div className="flex justify-between font-bold text-lg hard-border-t pt-2 mt-2"><span>Total</span><span className="tabular-nums">{bd.total.toFixed(2)} $</span></div>
            </div>
            <div className="space-y-3">
              <Field label="Compagnie *" value={form.sponsor_company} onChange={v => setForm({ ...form, sponsor_company: v })} testid="sponsor-company" />
              <Field label="Personne contact *" value={form.sponsor_contact_name} onChange={v => setForm({ ...form, sponsor_contact_name: v })} testid="sponsor-contact" />
              <Field label="Courriel *" type="email" value={form.sponsor_email} onChange={v => setForm({ ...form, sponsor_email: v })} testid="sponsor-email" />
              <Field label="Téléphone" type="tel" value={form.sponsor_phone} onChange={v => setForm({ ...form, sponsor_phone: v })} testid="sponsor-phone" />
              <Field label="Adresse de facturation" value={form.sponsor_address} onChange={v => setForm({ ...form, sponsor_address: v })} testid="sponsor-address" />
              <Field label="NEQ / Numéro d'entreprise" value={form.business_number} onChange={v => setForm({ ...form, business_number: v })} testid="sponsor-neq" />
              <div>
                <label className="ticker text-xs text-muted-foreground">Notes / Logo / Demandes</label>
                <textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} className="w-full hard-border px-3 py-2 mt-1" rows={3} data-testid="sponsor-notes" />
              </div>
            </div>
            <button
              onClick={submitOrder}
              disabled={submitting}
              className="w-full bg-foreground text-white py-3 ticker hover:bg-black transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              data-testid="confirm-order-btn"
            >
              {submitting ? "Création…" : "Continuer vers le paiement"}
              <ArrowRight size={18} weight="bold" />
            </button>
            <div className="text-xs text-muted-foreground">
              Aucun paiement encore. Tu pourras réviser le total avant de payer par carte.
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ---------- LANDING ----------
  return (
    <div className="min-h-screen bg-background" data-testid="commandites-landing">
      <Nav />
      <header className="hard-border-b">
        <div className="max-w-7xl mx-auto px-6 py-16 flex flex-col gap-4">
          <div className="ticker text-[#D90429]">COMMANDITES · GALA 16 MAI 2026</div>
          <h1 className="font-display font-black text-5xl sm:text-6xl lg:text-7xl tracking-tight">
            Soutiens la<br/>relève québécoise<span className="text-[#D90429]">.</span>
          </h1>
          <p className="text-base sm:text-lg text-muted-foreground max-w-2xl">
            En commanditant le Grand Prix Gladiateur du Nord, vous donnez à des athlètes québécois la chance de se hisser jusqu'aux scènes provinciale, nationale et mondiale. <strong>Votre commandite est 100 % déductible</strong> comme dépense de publicité commerciale.
          </p>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-12 space-y-12">
        {loading ? (
          <div className="text-center py-12 text-muted-foreground">Chargement…</div>
        ) : !plan || !plan.tiers?.length ? (
          <div className="hard-border bg-white p-12 text-center" data-testid="commandites-empty">Aucun plan actif pour le moment.</div>
        ) : (
          <>
            <section>
              <h2 className="font-display font-black text-3xl mb-6">Choisis ton niveau</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {plan.tiers.map((t) => {
                  const bd = breakdownFor(t.price);
                  return (
                    <div key={t.tier} className="hard-border bg-white p-5 flex flex-col gap-3" data-testid={`tier-${t.tier}`}>
                      <div className={`ticker text-xs px-2 py-1 inline-block w-fit ${TIER_COLORS[t.tier] || "hard-border"}`}>
                        <Trophy size={12} weight="fill" className="inline mr-1" />{t.tier}
                      </div>
                      <div>
                        <div className="font-display font-black text-3xl tabular-nums">{t.price.toLocaleString("fr-CA")} $</div>
                        <div className="text-xs text-muted-foreground mt-1">+ taxes = <strong>{bd.total.toFixed(2)} $ TTC</strong></div>
                      </div>
                      <p className="text-sm text-muted-foreground flex-1">{t.perks}</p>
                      <button
                        onClick={() => pickTier(t)}
                        className="bg-[#D90429] hover:bg-[#BF0424] text-white py-2 ticker transition-colors flex items-center justify-center gap-2"
                        data-testid={`pick-${t.tier}`}
                      >
                        Choisir <ArrowRight size={16} weight="bold" />
                      </button>
                    </div>
                  );
                })}
              </div>
            </section>

            <section className="hard-border bg-foreground text-white p-8 space-y-4">
              <div className="ticker text-white/60 flex items-center gap-2"><Heart size={14} weight="fill" /> SOUTIEN LIBRE</div>
              <h2 className="font-display font-black text-2xl">Tu veux soutenir avec un montant libre ?</h2>
              <p className="text-sm text-white/80">Chaque dollar va directement à la formation des athlètes et à l'organisation du gala. Mention sur nos réseaux + reçu de commandite officiel.</p>
              <div className="flex flex-col sm:flex-row gap-2 max-w-md">
                <div className="flex-1 hard-border bg-white text-black flex items-center px-3">
                  <span className="text-muted-foreground">$</span>
                  <input
                    type="number"
                    placeholder="100"
                    min="25"
                    step="5"
                    value={customAmount}
                    onChange={e => setCustomAmount(e.target.value)}
                    className="flex-1 px-2 py-2 outline-none bg-transparent"
                    data-testid="custom-amount"
                  />
                </div>
                <button
                  onClick={pickCustom}
                  className="bg-[#D90429] hover:bg-[#BF0424] text-white px-6 py-2 ticker transition-colors"
                  data-testid="custom-go"
                >
                  Soutenir
                </button>
              </div>
              <div className="text-xs text-white/50">Montant minimum : 25 $</div>
            </section>

            <section className="text-xs text-muted-foreground space-y-1">
              <div><strong>Émetteur :</strong> Institut Impact Élite Martial · 114 rue du Domaine-du-Repos · Val-d'Or (QC) J9P 0C3</div>
              <div><strong>Événement :</strong> {plan.event}</div>
              <div><strong>Lieu :</strong> {plan.venue}</div>
              <div><strong>Reçu fiscal :</strong> Reçu de commandite avec TPS/TVQ — déductible 100 % comme dépense de publicité commerciale (alinéa 18(1)(a) LIR).</div>
            </section>
          </>
        )}
      </main>
    </div>
  );
}

function Nav() {
  return (
    <nav className="sticky top-0 z-50 backdrop-blur-xl bg-white/70 hard-border-b">
      <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
        <Logo />
        <div className="flex items-center gap-3">
          <Link to="/" className="ticker text-muted-foreground hover:text-foreground" data-testid="nav-home">Accueil</Link>
          <Link to="/circuit" className="ticker text-muted-foreground hover:text-foreground" data-testid="nav-circuit">Circuit</Link>
          <Link to="/boutique" className="ticker text-muted-foreground hover:text-foreground" data-testid="nav-boutique">Boutique</Link>
        </div>
      </div>
    </nav>
  );
}

function Field({ label, type = "text", value, onChange, testid }) {
  return (
    <div>
      <label className="ticker text-xs text-muted-foreground">{label}</label>
      <input type={type} value={value} onChange={e => onChange(e.target.value)} className="w-full hard-border px-3 py-2 mt-1" data-testid={testid} />
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between hard-border-b py-1">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-bold">{value}</span>
    </div>
  );
}
